import { useState, useEffect } from "react";
import { accountSchemas, type accountTypes, type authenticationTypes } from "@pineseed/spec";
import { EntityTable, type Column } from "../components/EntityTable";
import { EntityForm } from "../components/EntityForm";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";
import { z } from "zod";

type Account = accountTypes["Account"];
type CasdoorUser = authenticationTypes["CasdoorUser"];
type View = 'accounts' | 'hierarchy' | 'users';

const LAST_ACCOUNTS_VIEW_KEY = 'admin-last-accounts-view';

// Minimal Casdoor user schemas for forms
const CreateCasdoorUserSchema = z.object({
  owner: z.string().default("built-in"),
  name: z.string().min(1).describe("Username"),
  displayName: z.string().min(1).describe("Display Name"),
  email: z.email(),
  type: z.string().default("normal-user"),
  isAdmin: z.boolean().default(false),
  isGlobalAdmin: z.boolean().default(false),
  isForbidden: z.boolean().default(false),
  isDeleted: z.boolean().default(false),
});

const UpdateCasdoorUserSchema = z.object({
  displayName: z.string().min(1).optional().describe("Display Name"),
  email: z.email().optional(),
  isAdmin: z.boolean().optional(),
  isGlobalAdmin: z.boolean().optional(),
  isForbidden: z.boolean().optional(),
});

export function AccountsPanel() {
  const { account: accountAdmin } = useAdmin();
  const { accounts, users } = accountAdmin;
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  
  const [view, setView] = useState<View>(() => {
    const saved = localStorage.getItem(LAST_ACCOUNTS_VIEW_KEY) as View | null;
    return saved || 'accounts';
  });

  // Persist view
  useEffect(() => {
    localStorage.setItem(LAST_ACCOUNTS_VIEW_KEY, view);
  }, [view]);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [hierarchyWorkspaceId, setHierarchyWorkspaceId] = useState<string>('');
  const [hierarchyData, setHierarchyData] = useState<accountTypes["WorkspaceHierarchy"] | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);

  // Users state
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState<CasdoorUser | null>(null);

  // CRUD handlers
  const handleCreate = async (data: Partial<Account>) => {
    try {
      await accounts.createEntity({
        name: data.name!,
        ...(data.email && { email: data.email }),
        ...(data.metadata && { metadata: data.metadata }),
      });
      setShowCreateForm(false);
    } catch (error) {
      console.error('Failed to create account:', error);
      throw error;
    }
  };

  const handleEdit = async (data: Partial<Account>) => {
    if (!editingAccount) return;
    try {
      await accounts.updateEntity(editingAccount.id, data);
      setEditingAccount(null);
    } catch (error) {
      console.error('Failed to update account:', error);
      throw error;
    }
  };

  const handleDelete = async (account: Account) => {
    if (!confirm(`Delete account "${account.name}"? This will cascade delete all organizations and workspaces.`)) {
      return;
    }
    try {
      await accounts.deleteEntity(account.id);
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  // Hierarchy lookup
  const handleHierarchyLookup = async () => {
    if (!hierarchyWorkspaceId.trim()) return;
    
    setHierarchyLoading(true);
    setHierarchyData(null);
    
    try {
      const hierarchy = await accountAdmin.workspaces.getWorkspaceHierarchy(hierarchyWorkspaceId);
      setHierarchyData(hierarchy);
    } catch (error) {
      console.error('Failed to get hierarchy:', error);
    } finally {
      setHierarchyLoading(false);
    }
  };

  // User CRUD handlers
  const handleCreateUser = async (data: Partial<CasdoorUser>) => {
    try {
      await users.createEntity(data);
      setShowCreateUserForm(false);
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  };

  const handleEditUser = async (data: Partial<CasdoorUser>) => {
    if (!editingUser) return;
    try {
      await users.updateEntity(editingUser.name, data);
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  };

  const handleDeleteUser = async (user: CasdoorUser) => {
    if (!confirm(`Delete user "${user.displayName || user.name}"?`)) {
      return;
    }
    try {
      await users.deleteEntity(user.name);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  // Table columns
  const accountColumns: Column<Account>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (account) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{account.name}</div>
          {account.email && (
            <div className="text-xs text-gray-500 dark:text-gray-400">{account.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'id',
      label: 'ID',
      render: (account) => (
        <CopyableText
          id={account.id}
          onCopy={copyToClipboard}
          isPulsing={isCopied(account.id)}
        >
          {account.id.slice(0, 8)}...
        </CopyableText>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (account) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {new Date(account.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (account) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setEditingAccount(account); }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(account); }}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const stats = accounts.getStats();
  const userStats = users.getStats();

  // User table columns
  const userColumns: Column<CasdoorUser>[] = [
    {
      key: 'displayName',
      label: 'Name',
      sortable: true,
      render: (user) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{user.displayName}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{user.name}</div>
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (user) => (
        <span className="text-sm text-gray-900 dark:text-gray-100">{user.email}</span>
      ),
    },
    {
      key: 'owner',
      label: 'Owner',
      render: (user) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">{user.owner}</span>
      ),
    },
    {
      key: 'isAdmin',
      label: 'Role',
      render: (user) => (
        <div className="flex gap-1">
          {user.isGlobalAdmin && (
            <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded">
              Global Admin
            </span>
          )}
          {user.isAdmin && !user.isGlobalAdmin && (
            <span className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 rounded">
              Admin
            </span>
          )}
          {user.isForbidden && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded">
              Forbidden
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (user) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setEditingUser(user); }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* View Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setView('accounts')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'accounts'
              ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400'
          }`}
        >
          Accounts ({stats.total})
        </button>
        <button
          onClick={() => setView('hierarchy')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'hierarchy'
              ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400'
          }`}
        >
          Hierarchy Lookup
        </button>
        <button
          onClick={() => setView('users')}
          className={`px-4 py-2 font-medium transition-colors ${
            view === 'users'
              ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400'
          }`}
        >
          Users (Casdoor) {userStats.total > 0 && `(${userStats.total})`}
        </button>
      </div>

      {/* Accounts View */}
      {view === 'accounts' && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Accounts</h2>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Create Account
            </button>
          </div>

          {accounts.error && (
            <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 p-4 rounded-lg">
              {accounts.error}
            </div>
          )}

          <EntityTable
            columns={accountColumns}
            data={accounts.entities}
            loading={accounts.loading}
          />
        </>
      )}

      {/* Hierarchy View */}
      {view === 'hierarchy' && (
        <>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Workspace Hierarchy Lookup</h2>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              <input
                type="text"
                value={hierarchyWorkspaceId}
                onChange={(e) => setHierarchyWorkspaceId(e.target.value)}
                placeholder="Enter Workspace ID"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
              <button
                onClick={handleHierarchyLookup}
                disabled={hierarchyLoading || !hierarchyWorkspaceId.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {hierarchyLoading ? 'Loading...' : 'Lookup'}
              </button>
            </div>
          </div>

          {hierarchyData && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Hierarchy for Workspace: {hierarchyData.workspaceName}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Account:</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{hierarchyData.accountName}</span>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-500">({hierarchyData.accountId})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Organization:</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{hierarchyData.organizationName}</span>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-500">({hierarchyData.organizationId})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-24">Workspace:</span>
                  <span className="text-sm text-gray-900 dark:text-gray-100">{hierarchyData.workspaceName}</span>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-500">({hierarchyData.workspaceId})</span>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Users View (Casdoor) */}
      {view === 'users' && (
        <>
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Users (Casdoor)</h2>
            <div className="flex gap-2">
              <button
                onClick={() => users.refetch()}
                disabled={users.loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
              >
                {users.loading ? 'Loading...' : 'Load Users'}
              </button>
              <button
                onClick={() => setShowCreateUserForm(true)}
                disabled={users.loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                Create User
              </button>
            </div>
          </div>

          {users.error && (
            <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 p-4 rounded-lg">
              {users.error}
            </div>
          )}

          {users.entities.length === 0 && !users.loading && (
            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 p-6 rounded-lg text-center">
              <p className="text-teal-700 dark:text-teal-300 font-medium mb-2">
                🔐 Secure User Management
              </p>
              <p className="text-sm text-teal-600 dark:text-teal-400">
                Click "Load Users" to authenticate and view Casdoor users.
                <br />
                Each operation requires popup authentication for security.
              </p>
            </div>
          )}

          {users.entities.length > 0 && (
            <EntityTable
              columns={userColumns}
              data={users.entities}
              loading={users.loading}
            />
          )}
        </>
      )}

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateUserForm}
        onClose={() => setShowCreateUserForm(false)}
        title="Create User (Casdoor)"
      >
        <EntityForm
          schema={CreateCasdoorUserSchema}
          onSubmit={handleCreateUser}
          onCancel={() => setShowCreateUserForm(false)}
        />
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
        title="Edit User (Casdoor)"
      >
        {editingUser && (
          <EntityForm
            schema={UpdateCasdoorUserSchema}
            initialData={editingUser}
            onSubmit={handleEditUser}
            onCancel={() => setEditingUser(null)}
          />
        )}
      </Modal>

      {/* Create Account Modal */}
      <Modal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        title="Create Account"
      >
        <EntityForm
          schema={accountSchemas.CreateAccountRequest}
          onSubmit={handleCreate}
          onCancel={() => setShowCreateForm(false)}
        />
      </Modal>

      {/* Edit Account Modal */}
      <Modal
        isOpen={!!editingAccount}
        onClose={() => setEditingAccount(null)}
        title="Edit Account"
      >
        {editingAccount && (
          <EntityForm
            schema={accountSchemas.UpdateAccountRequest}
            initialData={editingAccount}
            onSubmit={handleEdit}
            onCancel={() => setEditingAccount(null)}
          />
        )}
      </Modal>
    </div>
  );
}

